import { useState, useEffect, PropsWithChildren } from "react";
import { resendSignUpCode } from "aws-amplify/auth";
import { usePostHog } from "posthog-js/react";
import AuthLayout from "./AuthLayout";
import SignUpForm from "./SignUpForm";
import SignInForm from "./SignInForm";
import EmailCodeEntry from "./EmailCodeEntry";
import ForgotPasswordForm from "./ForgotPasswordForm";
import ResetPasswordForm from "./ResetPasswordForm";
import PageLoader from "../PageLoader";
import StaticHeader from "../../Product/Layout/Header/StaticHeader";
import { useUserSetup } from "./hooks/useUserSetup";
import { AuthEvent } from "../../../data/auth-events";
import styles from "./claimable-authenticator-v2.module.scss";

type AuthView =
  | "signUp"
  | "signIn"
  | "verifyEmail"
  | "forgotPassword"
  | "resetPassword";

const AUTH_VIEW_TO_EVENT: Record<AuthView, AuthEvent | null> = {
  signUp: AuthEvent.SignUp,
  signIn: AuthEvent.SignIn,
  verifyEmail: AuthEvent.ConfirmSignUp,
  forgotPassword: AuthEvent.ForgotPassword,
  resetPassword: AuthEvent.ConfirmResetPassword,
};

type ClaimableAuthenticatorV2Props = {
  hideSignUp?: boolean;
  defaultTab?: "signUp" | "signIn";
  prefilledEmail?: string;
  onLoginComplete?: () => void;
};

const ClaimableAuthenticatorV2 = ({
  children,
  hideSignUp = false,
  defaultTab = "signUp",
  prefilledEmail,
  onLoginComplete,
}: PropsWithChildren<ClaimableAuthenticatorV2Props>) => {
  const posthog = usePostHog();
  const { isAuthenticated, isLoading, error } = useUserSetup({
    onLoginComplete,
  });

  const [currentView, setCurrentView] = useState<AuthView>(
    hideSignUp ? "signIn" : defaultTab,
  );
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [showAccountVerified, setShowAccountVerified] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  useEffect(() => {
    const event = AUTH_VIEW_TO_EVENT[currentView];
    if (posthog && event) {
      posthog.capture(event);
    }
  }, [currentView, posthog]);

  if (error) {
    return (
      <div className="page_container">
        <div className={styles.claimableAuthenticatorV2}>
          <StaticHeader />
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>
              {
                "Failed to complete setting up your account. We're looking into it. If you don't hear from us, please contact us at "
              }
              <a href="mailto:support@getclaimable.com">
                {"support@getclaimable.com"}
              </a>
              {"."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.claimableAuthenticatorV2}>
        <StaticHeader />
        <PageLoader />
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const handleSignUpSuccess = (email: string) => {
    setPendingEmail(email);
    setCurrentView("verifyEmail");
  };

  const handleVerificationSuccess = () => {
    setShowAccountVerified(true);
    setCurrentView("signIn");
  };

  const handleForgotPassword = () => {
    setCurrentView("forgotPassword");
  };

  const handleForgotPasswordCodeSent = (email: string) => {
    setPendingEmail(email);
    setCurrentView("resetPassword");
  };

  const handlePasswordReset = () => {
    setShowPasswordReset(true);
    setCurrentView("signIn");
  };

  const handleRequireConfirmation = async (email: string) => {
    setPendingEmail(email);
    try {
      await resendSignUpCode({ username: email });
    } catch {}
    setCurrentView("verifyEmail");
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case "signUp":
        return (
          <SignUpForm
            onSignUpSuccess={handleSignUpSuccess}
            onSwitchToSignIn={() => setCurrentView("signIn")}
            prefilledEmail={prefilledEmail}
          />
        );

      case "signIn":
        return (
          <SignInForm
            onSignInSuccess={() => {}}
            onSwitchToSignUp={
              hideSignUp ? undefined : () => setCurrentView("signUp")
            }
            onForgotPassword={handleForgotPassword}
            onRequireConfirmation={handleRequireConfirmation}
            prefilledEmail={prefilledEmail}
            showAccountVerifiedMessage={showAccountVerified}
            showPasswordResetMessage={showPasswordReset}
          />
        );

      case "verifyEmail":
        return (
          <EmailCodeEntry
            email={pendingEmail}
            onVerified={handleVerificationSuccess}
          />
        );

      case "forgotPassword":
        return (
          <ForgotPasswordForm
            onCodeSent={handleForgotPasswordCodeSent}
            onBackToSignIn={() => setCurrentView("signIn")}
            prefilledEmail={prefilledEmail}
          />
        );

      case "resetPassword":
        return (
          <ResetPasswordForm
            email={pendingEmail}
            onPasswordReset={handlePasswordReset}
            onBackToSignIn={() => setCurrentView("signIn")}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.claimableAuthenticatorV2}>
      <AuthLayout>{renderCurrentView()}</AuthLayout>
    </div>
  );
};

export default ClaimableAuthenticatorV2;
